/**
 * Single DevTools HTTP client for board reads and Plan/Build/Review mutations.
 * Hits api-devtools `/devtools/v1/*` via `VITE_DEVTOOLS_API_URL`.
 * Auth is the member JWT held by AuthProvider.
 */
import type {
  Agent,
  AgentChatMessage,
  AgentSession,
  BoardResponse,
  ConsoleTimelineEvent,
  DeployEnvCell,
  Plan,
  PlanArtifact,
  PlanArtifactComment,
  PlanDiffResponse,
  UserProfile,
} from '../shared/types';
import type {
  RoadmapInitiative,
  RoadmapResponse,
  RoadmapVision,
} from '../shared/roadmapTypes';
import { getDevtoolsApiUrl } from '../utils/env';
import { getTokenFromStorage } from '../utils/tokenManager';
import { withRetry } from '../utils/withRetry';

/** Authenticated JSON fetch with retry; attaches Bearer token and surfaces HTTP status on Error. */
async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getTokenFromStorage();
  const exec = async () => {
    const res = await fetch(`${getDevtoolsApiUrl()}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      // Prefer API error/message/detail fields so UI toasts stay readable.
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
        detail?: string;
      };
      const err = new Error(
        body.error || body.message || body.detail || `HTTP ${res.status}`,
      ) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    if (res.status === 204) {
      return undefined as T;
    }
    return res.json() as Promise<T>;
  };
  return withRetry(exec);
}

/** Append camelCase projectId to a query string (factory resources require it). */
function withProjectQuery(path: string, projectId: string): string {
  if (!projectId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}projectId=${encodeURIComponent(projectId)}`;
}

/** Merge projectId into a JSON body for POST/PUT/PATCH factory calls. */
function withProjectBody(
  body: Record<string, unknown> | undefined,
  projectId: string,
): Record<string, unknown> {
  return { ...(body || {}), ...(projectId ? { projectId } : {}) };
}

export type CursorLaunchResponse = {
  kind: 'plan' | 'build' | 'fix_ci' | 'review' | string;
  agentId?: string | null;
  url?: string | null;
  specialistRole?: string | null;
  plan?: Plan | null;
};

/** Derived team connection / operator-action notices for the Information Center. */
export type SystemNotice = {
  noticeId: string;
  kind: string;
  severity: string;
  title: string;
  body: string;
  href?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  createdAt?: string | null;
};

/** Board reads + Plan/Build mutations on verb-style `/devtools/v1/*` paths. */
export const trackerApi = {
  board: (params: URLSearchParams, projectId?: string) => {
    if (projectId) params.set('projectId', projectId);
    return request<BoardResponse>(`/v1/board/search?${params}`);
  },
  /** Team system notices (git OAuth expiry, connection errors, …). */
  systemNotices: () =>
    request<{ notices: SystemNotice[] }>('/v1/system-notices/search'),
  changelog: (params: URLSearchParams, projectId?: string) => {
    if (projectId) params.set('projectId', projectId);
    return request<{ changelog: BoardResponse['changelog'] }>(
      `/v1/changelog/search?${params}`,
    );
  },
  plan: (id: string) => request<{ plan: Plan; agent?: Agent | null }>(`/v1/plan/${id}`),
  /** Plan-scoped unified event history (card History tab). */
  planEvents: (id: string, params?: { q?: string; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.limit != null) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return request<{ events: DevtoolsEvent[] }>(
      `/v1/plan/${encodeURIComponent(id)}/events${qs ? `?${qs}` : ''}`,
    );
  },
  /** Live PR diff, or an older saved PR set when `revision` is set (0 = newest previous). */
  diff: (id: string, revision?: number) =>
    request<PlanDiffResponse>(
      `/v1/plan/${id}/diff/get${revision == null ? '' : `?revision=${revision}`}`,
    ),
  /** Assign or clear epic parent via DevTools (proxies taskboard reparent). */
  setParent: (planId: string, parentId: string | null) =>
    request<{ plan: Plan }>(`/v1/plan/${planId}/parent/update`, {
      method: 'POST',
      body: JSON.stringify(
        parentId ? { parentId } : { clearParent: true },
      ),
    }),
  /** Replace Bitbucket PR URLs on a plan (requires claimed slot). */
  attachPrs: (planId: string, urls: string[]) =>
    request<{ plan: Plan }>(`/v1/plan/${planId}/prs/attach`, {
      method: 'POST',
      body: JSON.stringify({ urls }),
    }),
  /** Move card to blocked (needs human). */
  block: (planId: string, reason: string, blockedBy: 'agent' | 'staff' = 'staff') =>
    request<{ plan: Plan }>(`/v1/plan/${planId}/block`, {
      method: 'POST',
      body: JSON.stringify({ reason, blockedBy }),
    }),
  /** Staff Unblock — writes feedback, leaves blocked, relaunches Cursor. */
  unblock: (
    planId: string,
    feedback: string,
    resumeKind: 'plan' | 'build' | 'fix_ci',
  ) =>
    request<{ plan: Plan; cursor?: CursorLaunchResponse | null }>(
      `/v1/plan/${planId}/unblock`,
      {
        method: 'POST',
        body: JSON.stringify({ feedback, resumeKind }),
      },
    ),
  /** Staff cancel — terminal cancelled; releases ownership seats. */
  cancel: (planId: string, reason?: string) =>
    request<{ plan: Plan }>(`/v1/plan/${planId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(reason?.trim() ? { reason: reason.trim() } : {}),
    }),
  /** Front door — create plan from goal and launch Plan specialist. */
  dispatch: (title: string, goalMd: string, agentName?: string, projectId?: string) =>
    request<{
      planId: string;
      agentId: string;
      plan: Plan;
      cursor?: CursorLaunchResponse | null;
    }>('/v1/dispatch', {
      method: 'POST',
      body: JSON.stringify(
        withProjectBody(
          {
            title,
            goalMd,
            ...(agentName ? { agentName } : {}),
          },
          projectId || '',
        ),
      ),
    }),
  /** Load or lazy-create the caller's DevTools profile. */
  getMyProfile: () => request<{ profile: UserProfile }>('/v1/me/profile/get'),
  /** Settings save or Google login sync (`fillBlanksOnly`). */
  putMyProfile: (body: {
    displayName?: string;
    pictureUrl?: string;
    fillBlanksOnly?: boolean;
    bitbucketEmails?: string[];
  }) =>
    request<{ profile: UserProfile }>('/v1/me/profile/update', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Batch profile lookup, or list all when ``userIds`` is omitted/empty. */
  listProfiles: (userIds?: string[]) => {
    if (!userIds || userIds.length === 0) {
      return request<{ profiles: UserProfile[] }>('/v1/profiles/search');
    }
    const params = new URLSearchParams();
    for (const id of userIds) {
      params.append('userId', id);
    }
    return request<{ profiles: UserProfile[] }>(`/v1/profiles/search?${params}`);
  },
  /** Bitbucket emails for Settings multi-select (mapped + agent owners). */
  listBitbucketEmails: () =>
    request<{ emails: string[] }>('/v1/bitbucket-emails/search'),

  /** Support Slack connection status for Connections → Slack. */
  supportSlackStatus: (projectId: string) =>
    request<{
      connected: boolean;
      workspaces: Array<{
        slackTeamId: string;
        teamName: string;
        source: 'oauth' | 'env';
        botUserId: string;
        scope: string;
        monitoredCount: number;
      }>;
      source: 'oauth' | 'env' | null;
      teamName: string;
      slackTeamId: string;
      botUserId: string;
      scope: string;
      monitoredCount: number;
      oauthConfigured: boolean;
    }>(withProjectQuery('/v1/support/slack/status/get', projectId)),

  /** Slack OAuth authorize URL (browser redirect). */
  supportSlackOauthStart: (projectId: string) =>
    request<{ authorizeUrl: string; state: string }>(
      withProjectQuery('/v1/support/slack/oauth/start', projectId),
    ),

  /**
   * Channels available to the bot for one Slack workspace.
   * Appends slackTeamId so multi-workspace installs do not mix catalogs.
   * `channelCount` is the catalog size returned; `truncated` is true when the
   * API hit its safety limit (UI should warn — further pages are not fetched).
   */
  supportSlackAvailableChannels: (projectId: string, slackTeamId: string) => {
    const base = withProjectQuery('/v1/support/slack/available-channels/search', projectId);
    const sep = base.includes('?') ? '&' : '?';
    return request<{
      channels: Array<{
        channelId: string;
        channelName: string;
        isPrivate: boolean;
        isMember: boolean;
        slackTeamId?: string;
      }>;
      channelCount?: number;
      truncated?: boolean;
    }>(`${base}${sep}slackTeamId=${encodeURIComponent(slackTeamId)}`);
  },

  /** Monitored Support Slack channels (tagged with workspace). */
  supportSlackChannels: (projectId: string) =>
    request<{
      channels: Array<{
        teamId: string;
        projectId?: string;
        slackTeamId: string;
        teamName?: string;
        channelId: string;
        channelName: string;
        isPrivate: boolean;
        addedBy: string;
        dateAdded: string | null;
      }>;
    }>(withProjectQuery('/v1/support/slack/channels/search', projectId)),

  /** Batch-add monitored channels under a workspace (`channelIds[]` → `{ channels }`). */
  supportSlackAddChannels: (channelIds: string[], projectId: string, slackTeamId: string) =>
    request<{
      channels: Array<{
        teamId: string;
        slackTeamId: string;
        teamName?: string;
        channelId: string;
        channelName: string;
        isPrivate: boolean;
        addedBy: string;
        dateAdded: string | null;
      }>;
    }>('/v1/support/slack/channel/create', {
      method: 'POST',
      body: JSON.stringify(withProjectBody({ channelIds, slackTeamId }, projectId)),
    }),

  /**
   * Stop monitoring a channel (does not leave Slack).
   * Optional slackTeamId disambiguates when the same channel id exists across orgs.
   */
  supportSlackRemoveChannel: (channelId: string, projectId: string, slackTeamId?: string) => {
    let path = withProjectQuery(
      `/v1/support/slack/channel/${encodeURIComponent(channelId)}/delete`,
      projectId,
    );
    if (slackTeamId) {
      const sep = path.includes('?') ? '&' : '?';
      path = `${path}${sep}slackTeamId=${encodeURIComponent(slackTeamId)}`;
    }
    return request<{ ok: boolean; channelId: string }>(path, { method: 'POST' });
  },

  /** Disconnect an OAuth Slack workspace (not the env synthetic card). */
  supportSlackDisconnectWorkspace: (slackTeamId: string, projectId: string) =>
    request<{ ok: boolean; slackTeamId: string }>(
      withProjectQuery(
        `/v1/support/slack/workspace/${encodeURIComponent(slackTeamId)}/delete`,
        projectId,
      ),
      { method: 'POST' },
    ),
};

/** Plan/Build Cursor launches — same DevTools API host as tracker reads. */
export const agentsApi = {
  launchPlan: (planId: string) =>
    request<CursorLaunchResponse>(`/v1/plan/${planId}/plan`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
  launchBuild: (planId: string) =>
    request<CursorLaunchResponse>(`/v1/plan/${planId}/build`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
};

/**
 * Provider-agnostic agent sessions (Cursor / Claude / simulated chat).
 * Side effects: GET/POST against /v1/agent-session… verb paths; streamUrl is fetch-only (no request()).
 */
export const agentSessionsApi = {
  /** Load session metadata (name, provider, externalUrl). */
  get: (sessionId: string) =>
    request<{ session: AgentSession }>(`/v1/agent-session/${sessionId}`),
  /** Create a new agent session; optional planId binds chat to a tracker card. */
  create: (body: {
    prompt: string;
    name: string;
    provider?: string;
    planId?: string;
    script?: string;
  }) =>
    request<{ session: AgentSession }>('/v1/agent-session/create', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  listForPlan: (planId: string) =>
    request<{ sessions: AgentSession[] }>(
      `/v1/agent-sessions/search?planId=${encodeURIComponent(planId)}`,
    ),
  /** Transcript history for the in-app chat panel. */
  messages: (sessionId: string) =>
    request<{ messages: AgentChatMessage[] }>(
      `/v1/agent-session/${sessionId}/messages/search`,
    ),
  /** POST a user turn; returns runId used to open the SSE stream. */
  send: (sessionId: string, text: string) =>
    request<{ runId: string; sessionId: string; status: string }>(
      `/v1/agent-session/${sessionId}/message/create`,
      {
        method: 'POST',
        body: JSON.stringify({ text }),
      },
    ),
  /** Bearer-auth SSE URL (EventSource cannot set Authorization; use fetch). */
  streamUrl: (sessionId: string, runId: string) =>
    `${getDevtoolsApiUrl()}/v1/agent-session/${sessionId}/runs/${runId}/stream`,
};

/**
 * Plan-scoped named markdown artifacts + comments (Plan run workspace Artifacts tab).
 * Side effects: GET/POST/PATCH against /v1/plan/{id}/artifacts…; Send-to-session
 * posts a steer via the session message path on the API.
 */
export const planArtifactsApi = {
  // Sessions stay on agentSessionsApi.listForPlan — do not duplicate that path here.
  list: (planId: string) =>
    request<{ artifacts: PlanArtifact[] }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts`,
    ),
  get: (planId: string, artifactId: string) =>
    request<{ artifact: PlanArtifact }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}`,
    ),
  upsert: (
    planId: string,
    body: {
      fileName: string;
      bodyMd: string;
      title?: string;
      frontmatter?: Record<string, unknown>;
      sessionId?: string;
    },
  ) =>
    request<{ artifact: PlanArtifact }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  update: (
    planId: string,
    artifactId: string,
    body: {
      bodyMd?: string;
      title?: string;
      frontmatter?: Record<string, unknown>;
      sessionId?: string;
    },
  ) =>
    request<{ artifact: PlanArtifact }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  archive: (planId: string, artifactId: string) =>
    request<{ ok: boolean; artifactId: string }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}/archive`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  listComments: (planId: string, artifactId: string) =>
    request<{ comments: PlanArtifactComment[] }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}/comments`,
    ),
  createComment: (
    planId: string,
    artifactId: string,
    body: {
      bodyMd: string;
      kind?: string;
      replyToCommentId?: string;
      anchorJson?: Record<string, unknown>;
    },
  ) =>
    request<{ comment: PlanArtifactComment }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}/comments`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  resolveComment: (planId: string, artifactId: string, commentId: string) =>
    request<{ comment: PlanArtifactComment }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}/comments/${encodeURIComponent(commentId)}/resolve`,
      { method: 'POST', body: JSON.stringify({}) },
    ),
  sendToSession: (
    planId: string,
    artifactId: string,
    commentId: string,
    sessionId: string,
  ) =>
    request<{ comment: PlanArtifactComment; sessionSend: { runId: string; status: string } }>(
      `/v1/plan/${encodeURIComponent(planId)}/artifacts/${encodeURIComponent(artifactId)}/comments/${encodeURIComponent(commentId)}/send-to-session`,
      { method: 'POST', body: JSON.stringify({ sessionId }) },
    ),
};

/** One append-only Card Context Archive record (tool/subagent/envelope). */
export type CardContextRecord = {
  recordId: string;
  seq: number;
  kind: string;
  source: string;
  body: string;
  truncated?: boolean;
  blobRef?: string | null;
  sessionId?: string | null;
  forRecordId?: string | null;
  dateCreated?: string;
};

/**
 * Plan-scoped Card Context Archive client — full-fidelity agent context without compaction.
 * Side effects: authenticated GETs under /v1/plan/{id}/context/… (search, record, grep).
 */
export const cardContextApi = {
  /** Newest-first search; optional free-text `q`, kind filter, session scope. */
  search: (planId: string, opts?: { q?: string; kinds?: string; sessionId?: string; limit?: number }) => {
    const params = new URLSearchParams();
    if (opts?.q) params.set('q', opts.q);
    if (opts?.kinds) params.set('kinds', opts.kinds);
    if (opts?.sessionId) params.set('session_id', opts.sessionId);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return request<{ records: CardContextRecord[] }>(
      `/v1/plan/${encodeURIComponent(planId)}/context/search${qs ? `?${qs}` : ''}`,
    );
  },
  /** One record; server expands blob_ref bodies when present. */
  get: (planId: string, recordId: string) =>
    request<{ record: CardContextRecord }>(
      `/v1/plan/${encodeURIComponent(planId)}/context/records/${encodeURIComponent(recordId)}`,
    ),
  /** Literal substring scan across archived bodies (agent/debug tooling). */
  grep: (planId: string, pattern: string, limit = 40) =>
    request<{ hits: { recordId: string; seq: number; kind: string; line: string; byteOffset: number }[] }>(
      `/v1/plan/${encodeURIComponent(planId)}/context/grep?pattern=${encodeURIComponent(pattern)}&limit=${limit}`,
    ),
};

/** One tracked process in the Agent Console roster. */
export type ConsoleAgent = {
  consoleAgentId: string;
  externalAgentId?: string | null;
  presenceId?: string | null;
  sessionId?: string | null;
  planId?: string | null;
  /** Parent session when this roster row is a child under a plan (Cloudroom graph). */
  parentSessionId?: string | null;
  /** Provider external id of the parent session, when known. */
  parentExternalId?: string | null;
  ownerEmail: string;
  ownerLabel: string;
  displayName: string;
  framework: string;
  /** Console cloud adapter: cursor | claude | codex */
  adapter?: string;
  runtime: string;
  /** Durable Console host id when runtime=local. */
  hostId?: string | null;
  status: string;
  chatAvailable: boolean;
  externalUrl?: string | null;
  lastActiveAt?: string | null;
  planTitle?: string | null;
};

/** Owner bucket in the console roster search response. */
export type ConsoleOwnerGroup = {
  ownerEmail: string;
  ownerLabel: string;
  agents: ConsoleAgent[];
};

/** Console agent detail including plan markdown and git branches. */
export type ConsoleAgentDetail = {
  externalAgentId: string;
  displayName: string;
  status: string;
  framework: string;
  runtime: string;
  externalUrl?: string | null;
  latestRunId?: string | null;
  conversationMode?: string | null;
  git?: { branches?: Array<{ repoUrl?: string; branch?: string; prUrl?: string }> } | null;
  planId?: string | null;
  planTitle?: string | null;
  planMd?: string | null;
  chatAvailable: boolean;
};

/**
 * Agent Console BFF — multi-provider roster + plan/agent runs.
 * Side effects: GET/POST `/v1/console/agents…`; streamUrl is fetch-only.
 */
export const consoleApi = {
  search: (projectId: string) =>
    request<{ owners: ConsoleOwnerGroup[]; adapter: string; adapters?: string[] }>(
      withProjectQuery('/v1/console/agents/search', projectId),
    ),
  get: (externalAgentId: string, projectId: string) =>
    request<ConsoleAgentDetail>(
      withProjectQuery(
        `/v1/console/agents/${encodeURIComponent(externalAgentId)}`,
        projectId,
      ),
    ),
  messages: (externalAgentId: string, projectId: string) =>
    request<{
      messages: AgentChatMessage[];
      events?: ConsoleTimelineEvent[];
      source: string;
    }>(
      withProjectQuery(
        `/v1/console/agents/${encodeURIComponent(externalAgentId)}/messages`,
        projectId,
      ),
    ),
  // Idempotent create: clientRequestId + disposition let the API queue/interrupt/stop or replay.
  createRun: (
    externalAgentId: string,
    projectId: string,
    body: {
      text: string;
      mode: 'plan' | 'agent';
      clientRequestId?: string;
      disposition?: 'queue' | 'interrupt' | 'stop';
    },
  ) =>
    request<{
      runId: string;
      externalAgentId: string;
      mode: string;
      status: string;
      disposition?: string;
      replayed?: boolean;
    }>(`/v1/console/agents/${encodeURIComponent(externalAgentId)}/runs`, {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  streamUrl: (externalAgentId: string, runId: string, projectId: string) =>
    `${getDevtoolsApiUrl()}/v1/console/agents/${encodeURIComponent(externalAgentId)}/runs/${encodeURIComponent(runId)}/stream?projectId=${encodeURIComponent(projectId)}`,
};

/** True when Plan button should launch (planning card). */
export function canLaunchPlan(plan: Pick<Plan, 'status'>): boolean {
  return plan.status === 'planning';
}

/** True when Build button should launch (plan_ready card). */
export function canLaunchBuild(plan: Pick<Plan, 'status'>): boolean {
  return plan.status === 'plan_ready';
}

/* Coding Agents + Automations */

/** Cursor (or future) coding-agent registry row returned by `/v1/coding-agents/search`. */
export type CodingAgent = {
  codingAgentId: string;
  teamId: string;
  name: string;
  provider: string;
  hasApiKey: boolean;
  archived: boolean;
  createdBy: string;
  /** null = team-shared agent; set = user-bound ("mine"). */
  ownerUserId?: string | null;
  /** Team-only project default used by Dispatcher launches. */
  isDefault?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type CodingAgentOwnership = 'all' | 'mine' | 'team';

/** Team member row from GET /v1/team/members. */
export type TeamMember = {
  team_id: string;
  user_id: string;
  role: string;
  email?: string | null;
  display_name?: string | null;
  date_created?: string;
  date_updated?: string;
};

export type AutomationTrigger = {
  triggerId: string;
  automationId: string;
  kind: string;
  config: Record<string, unknown>;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AutomationAction = {
  actionId: string;
  automationId: string;
  kind: string;
  config: Record<string, unknown>;
  position: number;
  createdAt?: string | null;
  updatedAt?: string | null;
};

/** One row in the unified DevTools event log (plans, PRs, agents, sensors). */
export type DevtoolsEvent = {
  eventId: string;
  teamId?: string;
  projectId?: string | null;
  kind: string;
  identity: string;
  source: string;
  payload: Record<string, unknown>;
  planId?: string | null;
  epicId?: string | null;
  prRepo?: string | null;
  prNumber?: number | null;
  prUrl?: string | null;
  agentOwnerKind?: string | null;
  agentOwnerId?: string | null;
  agentInstanceId?: string | null;
  actorType?: string | null;
  summary?: string | null;
  createdAt?: string | null;
  dateCreated?: string | null;
};

export type EventSearchParams = {
  kind?: string;
  source?: string;
  q?: string;
  limit?: number;
  projectId?: string;
  planId?: string;
  epicId?: string;
  pr?: string;
  agentOwner?: string;
  agentId?: string;
};

export type Automation = {
  automationId: string;
  teamId: string;
  name: string;
  active: boolean;
  codingAgentId?: string | null;
  codingAgentName?: string | null;
  instructions: string;
  boardKind?: string | null;
  createdBy: string;
  archived: boolean;
  triggers?: AutomationTrigger[];
  actions?: AutomationAction[];
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type AutomationRun = {
  runId: string;
  automationId: string;
  triggerId?: string | null;
  eventId?: string | null;
  status: string;
  triggerKind: string;
  identity: string;
  event: Record<string, unknown>;
  sessionId?: string | null;
  planIds: string[];
  error: string;
  searchText: string;
  chat?: { role: string; text: string }[];
  createdAt?: string | null;
  updatedAt?: string | null;
};

/**
 * Coding Agents registry CRUD (verb-style `/v1/coding-agent…` paths).
 * API keys are write-only; responses expose `hasApiKey` only.
 */
export type GitProvider = {
  gitProviderId: string;
  provider: 'bitbucket' | 'github' | string;
  displayName: string;
  externalOwner: string;
  authMode: string;
  hasCredentials: boolean;
  monitorAll: boolean;
  status: string;
  lastError: string;
  monitoredCount: number;
  oauthConfigured: boolean;
  requiredScopes: string[];
};

export type GitRepoRow = {
  repoKey: string;
  name: string;
  externalId?: string;
  defaultBranch?: string;
  htmlUrl?: string;
  isPrivate?: boolean;
};

export type GitOwnerRow = {
  ownerKey: string;
  externalId?: string;
  name: string;
  htmlUrl?: string;
  avatarUrl?: string;
};

export type ProjectGitRepoRow = GitRepoRow & {
  gitProviderId: string;
  provider: string;
  addedBy?: string;
  dateAdded?: string;
  deploysStaging?: boolean | null;
  deploysProduction?: boolean | null;
  deployProfileSource?: string | null;
  deployProfileCheckedAt?: string | null;
};

/** Team-scoped Git provider registry. Tokens are write-only (`hasCredentials` on read). */
export const gitReposApi = {
  list: () => request<{ providers: GitProvider[] }>('/v1/git-providers/search'),
  create: (body: {
    provider: string;
    displayName?: string;
    externalOwner: string;
    email?: string;
    apiToken?: string;
    monitorAll?: boolean;
  }) =>
    request<{ provider: GitProvider }>('/v1/git-provider/create', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    id: string,
    body: {
      displayName?: string;
      externalOwner?: string;
      email?: string;
      apiToken?: string;
      monitorAll?: boolean;
    },
  ) =>
    request<{ provider: GitProvider }>(`/v1/git-provider/${encodeURIComponent(id)}/update`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  archive: (id: string) =>
    request<{ ok: boolean }>(`/v1/git-provider/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    }),
  status: (id: string) =>
    request<{ provider: GitProvider }>(`/v1/git-provider/${encodeURIComponent(id)}/status/get`),
  oauthStart: (id: string) =>
    request<{ authorizeUrl: string }>(`/v1/git-provider/${encodeURIComponent(id)}/oauth/start`),
  owners: (id: string) =>
    request<{ owners: GitOwnerRow[] }>(
      `/v1/git-provider/${encodeURIComponent(id)}/owners/available/search`,
    ),
  available: (id: string) =>
    request<{ repos: GitRepoRow[] }>(
      `/v1/git-provider/${encodeURIComponent(id)}/repos/available/search`,
    ),
  monitored: (id: string) =>
    request<{ repos: GitRepoRow[]; monitorAll: boolean }>(
      `/v1/git-provider/${encodeURIComponent(id)}/repos/monitored/search`,
    ),
  addRepos: (id: string, repoKeys: string[]) =>
    request<{ repos: GitRepoRow[]; monitorAll: boolean }>(
      `/v1/git-provider/${encodeURIComponent(id)}/repo/create`,
      { method: 'POST', body: JSON.stringify({ repoKeys }) },
    ),
  removeRepo: (id: string, repoKey: string) => {
    const [owner, name] = repoKey.split('/');
    return request<{ repos: GitRepoRow[] }>(
      `/v1/git-provider/${encodeURIComponent(id)}/repo/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/delete`,
      { method: 'POST' },
    );
  },
  monitorAll: (id: string, monitorAll: boolean) =>
    request<{ provider: GitProvider }>(
      `/v1/git-provider/${encodeURIComponent(id)}/monitor-all/update`,
      { method: 'POST', body: JSON.stringify({ monitorAll }) },
    ),
};

/**
 * Active-project monitored repositories. Mutations also maintain the project's
 * legacy short `repo_slugs` index server-side for board/story compatibility.
 */
export const projectGitReposApi = {
  list: (projectId: string) =>
    request<{ projectId: string; repos: ProjectGitRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/git-repos/monitored/search`,
    ),
  add: (
    projectId: string,
    body: { gitProviderId: string; repoKeys: string[] },
  ) =>
    request<{ projectId: string; repos: ProjectGitRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/git-repo/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  remove: (projectId: string, owner: string, name: string) =>
    request<{ projectId: string; repos: ProjectGitRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/git-repo/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/delete`,
      { method: 'POST' },
    ),
  /** Manual deploy-profile override (none / staging / production / both). */
  updateDeployProfile: (
    projectId: string,
    owner: string,
    name: string,
    body: { deploysStaging: boolean; deploysProduction: boolean },
  ) =>
    request<{ projectId: string; repos: ProjectGitRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/git-repo/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/deploy-profile/update`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Re-run server-side deploy-profile detection for one monitored repo. */
  analyzeDeployProfile: (projectId: string, owner: string, name: string) =>
    request<{ projectId: string; repos: ProjectGitRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/git-repo/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/deploy-profile/analyze`,
      { method: 'POST' },
    ),
};

export const codingAgentsApi = {
  list: (projectId: string, ownership: CodingAgentOwnership = 'all') => {
    let path = withProjectQuery('/v1/coding-agents/search', projectId);
    if (ownership && ownership !== 'all') {
      path += `${path.includes('?') ? '&' : '?'}ownership=${encodeURIComponent(ownership)}`;
    }
    return request<{ codingAgents: CodingAgent[] }>(path);
  },
  get: (id: string, projectId: string) =>
    request<{ codingAgent: CodingAgent }>(
      withProjectQuery(`/v1/coding-agent/${encodeURIComponent(id)}`, projectId),
    ),
  create: (
    body: {
      name: string;
      provider?: string;
      apiKey?: string;
      ownership?: 'mine' | 'team';
      ownerUserId?: string | null;
    },
    projectId: string,
  ) =>
    request<{ codingAgent: CodingAgent }>('/v1/coding-agent/create', {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  update: (
    id: string,
    body: {
      name?: string;
      provider?: string;
      apiKey?: string;
      ownership?: 'mine' | 'team';
      ownerUserId?: string | null | 'team';
    },
  ) =>
    request<{ codingAgent: CodingAgent }>(
      `/v1/coding-agent/${encodeURIComponent(id)}/update`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),
  remove: (id: string) =>
    request<{ ok: boolean }>(`/v1/coding-agent/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    }),
  /** Sets or clears the project's Team default; the API clears any sibling default. */
  setDefault: (id: string, projectId: string, isDefault = true) =>
    request<{ codingAgent: CodingAgent }>(
      `/v1/coding-agent/${encodeURIComponent(id)}/set-default`,
      {
        method: 'POST',
        body: JSON.stringify(withProjectBody({ isDefault }, projectId)),
      },
    ),
};

/** Tenant team members admin (list / add / remove). */
export const teamMembersApi = {
  list: () => request<{ members: TeamMember[]; teamId: string }>('/v1/team/members'),
  add: (body: { email?: string; user_id?: string; role?: string }) =>
    request<TeamMember>('/v1/team/members', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  remove: (userId: string) =>
    request<{ ok: boolean }>(`/v1/team/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
};

/**
 * Automations CRUD, triggers, and run history (verb-style `/v1/automation…` paths).
 * Trigger kinds match the frontend catalog; runs power the History tab.
 */
export const automationsApi = {
  list: (projectId: string) =>
    request<{ automations: Automation[] }>(
      withProjectQuery('/v1/automations/search', projectId),
    ),
  get: (id: string, projectId: string) =>
    request<{ automation: Automation }>(
      withProjectQuery(`/v1/automation/${encodeURIComponent(id)}`, projectId),
    ),
  create: (
    body: {
      name?: string;
      instructions?: string;
      codingAgentId?: string | null;
      boardKind?: string | null;
      active?: boolean;
    },
    projectId: string,
  ) =>
    request<{ automation: Automation }>('/v1/automation/create', {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  update: (
    id: string,
    body: {
      name?: string;
      instructions?: string;
      codingAgentId?: string | null;
      clearCodingAgent?: boolean;
      boardKind?: string | null;
      active?: boolean;
    },
  ) =>
    request<{ automation: Automation }>(
      `/v1/automation/${encodeURIComponent(id)}/update`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    ),
  remove: (id: string) =>
    request<{ ok: boolean }>(`/v1/automation/${encodeURIComponent(id)}/archive`, {
      method: 'POST',
    }),
  addTrigger: (id: string, body: { kind: string; config?: Record<string, unknown> }) =>
    request<{ trigger: AutomationTrigger }>(
      `/v1/automation/${encodeURIComponent(id)}/trigger/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateTrigger: (
    id: string,
    triggerId: string,
    body: { kind?: string; config?: Record<string, unknown> },
  ) =>
    request<{ trigger: AutomationTrigger }>(
      `/v1/automation/${encodeURIComponent(id)}/trigger/${encodeURIComponent(triggerId)}/update`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  deleteTrigger: (id: string, triggerId: string) =>
    request<{ ok: boolean }>(
      `/v1/automation/${encodeURIComponent(id)}/trigger/${encodeURIComponent(triggerId)}/delete`,
      { method: 'POST' },
    ),
  addAction: (
    id: string,
    body: { kind: string; config?: Record<string, unknown>; position?: number },
  ) =>
    request<{ action: AutomationAction }>(
      `/v1/automation/${encodeURIComponent(id)}/action/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  updateAction: (
    id: string,
    actionId: string,
    body: { kind?: string; config?: Record<string, unknown>; position?: number },
  ) =>
    request<{ action: AutomationAction }>(
      `/v1/automation/${encodeURIComponent(id)}/action/${encodeURIComponent(actionId)}/update`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  deleteAction: (id: string, actionId: string) =>
    request<{ ok: boolean }>(
      `/v1/automation/${encodeURIComponent(id)}/action/${encodeURIComponent(actionId)}/delete`,
      { method: 'POST' },
    ),
  /** Project-wide event search (Automations → Events). */
  listEvents: (params?: EventSearchParams) => {
    const sp = new URLSearchParams();
    if (params?.kind) sp.set('kind', params.kind);
    if (params?.source) sp.set('source', params.source);
    if (params?.q) sp.set('q', params.q);
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.projectId) sp.set('projectId', params.projectId);
    if (params?.planId) sp.set('planId', params.planId);
    if (params?.epicId) sp.set('epicId', params.epicId);
    if (params?.pr) sp.set('pr', params.pr);
    if (params?.agentOwner) sp.set('agentOwner', params.agentOwner);
    if (params?.agentId) sp.set('agentId', params.agentId);
    const qs = sp.toString();
    return request<{ events: DevtoolsEvent[] }>(
      `/v1/events/search${qs ? `?${qs}` : ''}`,
    );
  },
  listRuns: (
    id: string,
    params?: { q?: string; status?: string; triggerKind?: string },
  ) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set('q', params.q);
    if (params?.status) sp.set('status', params.status);
    if (params?.triggerKind) sp.set('triggerKind', params.triggerKind);
    const qs = sp.toString();
    return request<{ runs: AutomationRun[] }>(
      `/v1/automation/${encodeURIComponent(id)}/runs/search${qs ? `?${qs}` : ''}`,
    );
  },
  getRun: (id: string, runId: string) =>
    request<{ run: AutomationRun }>(
      `/v1/automation/${encodeURIComponent(id)}/run/${encodeURIComponent(runId)}`,
    ),
};

/** LABS: live agent presence row for Comms wake (push / pull pending). */
export type AgentPresence = {
  presenceId: string;
  teamId: string;
  framework: string;
  runtime: string;
  displayName: string;
  ownerEmail: string;
  planId?: string | null;
  agentSessionId?: string | null;
  promptChannel: string;
  status: string;
  ttlSeconds: number;
  pendingCount: number;
  alwaysOn: boolean;
  monitorConversationIds: string[];
  responsibility?: string;
  skills?: string[];
  repos?: string[];
  heartbeatAt?: string | null;
};

/** LABS: Comms discussion thread. Committed work lives on the ledger, not in this row. */
export type CommsConversation = {
  conversationId: string;
  teamId: string;
  title: string;
  kind: string;
  linkedPlanId?: string | null;
  createdBy: string;
  unreadCount: number;
  lastMessageAt?: string | null;
  participants?: CommsParticipant[];
  createdAt?: string | null;
  updatedAt?: string | null;
};

/** Human or agent member of a Comms conversation. */
export type CommsParticipant = {
  participantId: string;
  conversationId: string;
  kind: string;
  presenceId?: string | null;
  userId?: string | null;
  role: string;
  durableMonitor: boolean;
};

/**
 * One committed piece of work on a Comms thread.
 * Agents claim it, keep a single write scope, and finish only with a verification note.
 */
export type CommsWorkItem = {
  workItemId: string;
  conversationId: string;
  title: string;
  brief: {
    goal?: string;
    constraints?: string[];
    decisions?: string[];
    openQuestions?: string[];
    nonGoals?: string[];
  };
  state: string;
  ownerPresenceId?: string | null;
  writeScope: string[];
  dependsOn: string[];
  doneWhen: string;
  artifactRef?: string;
  verification?: string;
  blockedReason?: string;
  claimable: boolean;
};

/** Single Comms message; `unread` is relative to the authenticated viewer. */
export type CommsMessage = {
  messageId: string;
  conversationId: string;
  authorKind: string;
  authorId: string;
  body: string;
  role: string;
  unread?: boolean;
  createdAt?: string | null;
};

/**
 * LABS: agent presence registry for Comms wake / mention pickers.
 * Side effects: POST register/heartbeat; GET search/pending against /v1/agent-presence….
 */
export const presenceApi = {
  /** Register or refresh this runtime's presence row. */
  register: (body: Record<string, unknown>) =>
    request<{ presence: AgentPresence }>('/v1/agent-presence/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** List presence rows; `onlineOnly` filters to heartbeats still within TTL. */
  list: (onlineOnly = false) =>
    request<{ presences: AgentPresence[] }>(
      `/v1/agent-presence/search?onlineOnly=${onlineOnly ? 'true' : 'false'}`,
    ),
  /** Extend TTL for an existing presence id. */
  heartbeat: (id: string) =>
    request<{ presence: AgentPresence }>(
      `/v1/agent-presence/${encodeURIComponent(id)}/heartbeat`,
      { method: 'POST' },
    ),
  /** Pull queued wake prompts for a pull-channel presence. */
  drainPending: (id: string) =>
    request<{ presenceId: string; prompts: Record<string, unknown>[] }>(
      `/v1/agent-presence/${encodeURIComponent(id)}/pending`,
    ),
};

/**
 * LABS: Comms discussion board (agent↔human threads, unread, mention wake).
 * Side effects: GET/POST against /v1/comms… verb paths; markRead clears viewer unread.
 */
export const commsApi = {
  /** Unread conversation/message counts for the authenticated user (TopBar badge). */
  unread: () =>
    request<{
      unreadConversations: number;
      unreadMessages: number;
      conversations: { conversationId: string; unreadCount: number }[];
    }>('/v1/comms/unread'),
  /** Conversation list for the Comms sidebar. */
  list: () =>
    request<{ conversations: CommsConversation[] }>('/v1/comms/conversations/search'),
  /** Load one conversation metadata row. */
  get: (id: string) =>
    request<{ conversation: CommsConversation }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}`,
    ),
  /** Create a discussion thread; navigates to the new id from the SPA. */
  create: (body: {
    title: string;
    kind?: string;
    linkedPlanId?: string;
    ownerPresenceId?: string;
    humanUserIds?: string[];
  }) =>
    request<{ conversation: CommsConversation }>('/v1/comms/conversation/create', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /** Chronological messages for an open thread. */
  messages: (id: string) =>
    request<{ messages: CommsMessage[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/messages/search`,
    ),
  /** Post a human/agent message; may wake durable monitors. */
  postMessage: (
    id: string,
    body: { body: string; authorKind?: string; role?: string; wakeMonitors?: boolean },
  ) =>
    request<{ message: CommsMessage; wakeResults: unknown[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/message/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Pull online agents (or humans) into the thread and optionally wake them. */
  mention: (
    id: string,
    body: { presenceIds?: string[]; humanUserIds?: string[]; note?: string },
  ) =>
    request<Record<string, unknown>>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/mention`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Mark the thread read up to `messageId` (defaults to latest when omitted). */
  markRead: (id: string, messageId?: string) =>
    request<Record<string, unknown>>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/read`,
      { method: 'POST', body: JSON.stringify({ messageId }) },
    ),
  /** Add a participant without a full mention wake cycle. */
  addParticipant: (
    id: string,
    body: {
      kind: string;
      role?: string;
      presenceId?: string;
      userId?: string;
      durableMonitor?: boolean;
    },
  ) =>
    request<{ participant: CommsParticipant }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/participant/add`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Shared ledger for this thread. Agents poll this instead of replaying chat. */
  workItems: (id: string) =>
    request<{ workItems: CommsWorkItem[] }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/work-items/search`,
    ),
  /** Propose unclaimed work. `doneWhen` is the termination condition peers can check. */
  proposeWork: (
    id: string,
    body: {
      title: string;
      brief: { goal: string };
      doneWhen: string;
      writeScope?: string[];
    },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/work-item/create`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Assign work to one agent with a short brief. Does not copy the transcript. */
  handoff: (
    id: string,
    body: {
      toPresenceId: string;
      title: string;
      brief: { goal: string };
      doneWhen: string;
      writeScope?: string[];
    },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/handoff`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Atomic self-claim. Fails when dependencies are open or the write scope is taken. */
  claimWork: (id: string, presenceId: string) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/work-item/${encodeURIComponent(id)}/claim`,
      { method: 'POST', body: JSON.stringify({ presenceId }) },
    ),
  /** Owner status edge. `done` requires verification or an artifact ref. */
  workStatus: (
    id: string,
    body: { presenceId?: string; state: string; verification?: string; artifactRef?: string; reason?: string },
  ) =>
    request<{ workItem: CommsWorkItem }>(
      `/v1/comms/work-item/${encodeURIComponent(id)}/status`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /** Ask an agent to propose a work split for this discussion. */
  divideWork: (id: string, body: { presenceId: string; prompt: string; provider?: string }) =>
    request<Record<string, unknown>>(
      `/v1/comms/conversation/${encodeURIComponent(id)}/divide-work`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
};

/** LABS: Failed default-branch pipelines (build / test / staging / production). */
export type FailedPipeline = {
  workspace: string;
  repo: string;
  repoKey: string;
  buildNumber: number;
  branch: string;
  commit: string;
  pipelineUrl: string;
  stepName: string;
  reason: string;
  finishedAt?: string | null;
};

export const pipelinesApi = {
  search: (params?: { reason?: string; q?: string; limit?: number }) => {
    const sp = new URLSearchParams();
    if (params?.reason) sp.set('reason', params.reason);
    if (params?.q) sp.set('q', params.q);
    if (params?.limit != null) sp.set('limit', String(params.limit));
    const qs = sp.toString();
    return request<{ enabled: boolean; pipelines: FailedPipeline[] }>(
      `/v1/pipelines/search${qs ? `?${qs}` : ''}`,
    );
  },
  refresh: () =>
    request<{ enabled: boolean; scanned: number; recorded: number }>(
      '/v1/pipelines/refresh',
      { method: 'POST', body: '{}' },
    ),
};

/** Project Deployments timeline (monitored repos × plan-card PRs). */
export type DeploymentEntry = {
  planId: string;
  planTitle: string;
  status: string;
  updatedAt: string;
  fullyDeployed: boolean;
  pr: {
    repo?: string | null;
    bitbucketId?: number | null;
    url?: string | null;
    state?: string | null;
    title?: string | null;
    authorName?: string | null;
    commitHash?: string | null;
  };
  /** Reuse board DeployEnvCell so DeployEnvTags accepts entry staging/production. */
  staging?: DeployEnvCell | null;
  production?: DeployEnvCell | null;
};

export type DeploymentRepoRow = {
  repoKey: string;
  name: string;
  htmlUrl?: string;
  deployProfile: {
    label: string;
    deploysStaging?: boolean | null;
    deploysProduction?: boolean | null;
    source?: string | null;
  };
  activityAt?: string | null;
  entries: DeploymentEntry[];
};

export const deploymentsApi = {
  /**
   * Load the project Deployments timeline (monitored repos × plan-card PRs).
   * `sort=name` orders by repo key; `sort=activity` by newest entry first.
   */
  search: (projectId: string, sort: 'name' | 'activity' = 'name') => {
    const sp = new URLSearchParams({ sort });
    return request<{ projectId: string; sort: string; repos: DeploymentRepoRow[] }>(
      `/v1/project/${encodeURIComponent(projectId)}/deployments/search?${sp.toString()}`,
    );
  },
};

/** Factory roadmap — vision, sequenced initiatives, dependsOn graph. */
export const roadmapApi = {
  get: (projectId: string) =>
    request<RoadmapResponse>(withProjectQuery('/v1/roadmap', projectId)),
  getVision: (projectId: string) =>
    request<RoadmapVision>(withProjectQuery('/v1/roadmap/vision', projectId)),
  putVision: (visionMd: string, projectId: string) =>
    request<RoadmapVision>('/v1/roadmap/vision', {
      method: 'PUT',
      body: JSON.stringify(withProjectBody({ visionMd }, projectId)),
    }),
  createInitiative: (
    body: {
      title: string;
      summaryMd?: string;
      productArea?: string;
    },
    projectId: string,
  ) =>
    request<RoadmapInitiative>('/v1/roadmap/initiatives', {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  patchInitiative: (
    initiativeId: string,
    body: Record<string, unknown>,
    projectId: string,
  ) =>
    request<RoadmapInitiative>(
      withProjectQuery(`/v1/roadmap/initiatives/${initiativeId}`, projectId),
      {
        method: 'PATCH',
        body: JSON.stringify(withProjectBody(body, projectId)),
      },
    ),
  archiveInitiative: (initiativeId: string, projectId: string) =>
    request<RoadmapInitiative>(
      withProjectQuery(`/v1/roadmap/initiatives/${initiativeId}`, projectId),
      { method: 'DELETE' },
    ),
  setDependsOn: (initiativeId: string, dependsOn: string[], projectId: string) =>
    request<RoadmapInitiative>(
      withProjectQuery(
        `/v1/roadmap/initiatives/${initiativeId}/depends-on`,
        projectId,
      ),
      {
        method: 'PUT',
        body: JSON.stringify(withProjectBody({ dependsOn }, projectId)),
      },
    ),
  attachEpic: (initiativeId: string, epicItemId: string, projectId: string) =>
    request<{ initiativeId: string; epicIds: string[] }>(
      withProjectQuery(
        `/v1/roadmap/initiatives/${initiativeId}/epics`,
        projectId,
      ),
      {
        method: 'POST',
        body: JSON.stringify(withProjectBody({ epicItemId }, projectId)),
      },
    ),
  detachEpic: (initiativeId: string, epicItemId: string, projectId: string) =>
    request<{ initiativeId: string; epicIds: string[] }>(
      withProjectQuery(
        `/v1/roadmap/initiatives/${initiativeId}/epics/${epicItemId}`,
        projectId,
      ),
      { method: 'DELETE' },
    ),
  reorder: (
    items: Array<{ initiativeId: string; sequence: number; layoutY?: number }>,
    projectId: string,
  ) =>
    request<RoadmapResponse>('/v1/roadmap/reorder', {
      method: 'POST',
      body: JSON.stringify(withProjectBody({ items }, projectId)),
    }),
  suggestSequence: (projectId: string) =>
    request<{
      suggested: Array<{
        initiativeId: string;
        title?: string;
        sequence: number;
      }>;
    }>('/v1/roadmap/suggest-sequence', {
      method: 'POST',
      body: JSON.stringify(withProjectBody({}, projectId)),
    }),
  dispatch: (
    initiativeId: string,
    opts: { force?: boolean; epicItemId?: string } | undefined,
    projectId: string,
  ) =>
    request<{
      initiativeId: string;
      epicId?: string | null;
      dispatch: {
        planId: string;
        agentId: string;
        plan: Plan;
        cursor?: CursorLaunchResponse | null;
      };
    }>(
      withProjectQuery(
        `/v1/roadmap/initiatives/${initiativeId}/dispatch`,
        projectId,
      ),
      {
        method: 'POST',
        body: JSON.stringify(
          withProjectBody(
            {
              force: opts?.force ?? false,
              ...(opts?.epicItemId ? { epicItemId: opts.epicItemId } : {}),
            },
            projectId,
          ),
        ),
      },
    ),
};

/** Living User Stories — team-scoped product intent SoR. */
export type StoryScenario = {
  name?: string;
  given?: string;
  when?: string;
  then?: string;
};

export type StoryDocument = {
  documentId: string;
  teamId: string;
  projectId: string;
  kind: string;
  slug: string;
  title: string;
  status: string;
  bodyMd: string;
  actor?: string | null;
  action?: string | null;
  result?: string | null;
  scenarios?: StoryScenario[];
  /** @deprecated dual-read alias for actor */
  audience?: string | null;
  /** @deprecated dual-read alias for action */
  intent?: string | null;
  /** @deprecated dual-read alias for result */
  success?: string | null;
  outOfScope?: string | null;
  openQuestions?: string | null;
  /** Repo slugs this story verifies (project.repo_slugs subset). */
  surfaces?: string[];
  /** Prerequisite group applied before QA runs this story. */
  prereqId?: string | null;
  /** Product-doc section provenance. */
  sourceSectionIds?: string[];
  /** Product-doc behavior provenance. */
  sourceBehaviorIds?: string[];
  aiDesign?: string | null;
  aiInvestigation?: string | null;
  aiSummary?: string | null;
  publishedRevisionId?: string | null;
  revisionId?: string;
  source?: string;
  planId?: string | null;
  changeId?: string | null;
  dateCreated?: string;
  dateUpdated?: string;
};

export type StoryChangeOp = {
  opId: string;
  changeId: string;
  op: 'add' | 'modify' | 'archive' | string;
  documentId?: string | null;
  beforeRevisionId?: string | null;
  kind?: string | null;
  slug?: string | null;
  proposed?: Record<string, unknown>;
  sortOrder?: number;
};

export type StoryChange = {
  changeId: string;
  teamId: string;
  projectId: string;
  planId?: string | null;
  title: string;
  rationale?: string;
  status: string;
  autoApplied?: boolean;
  deviation?: boolean;
  /** Human gate when change touches established product behaviors. */
  requiresBehaviorConfirm?: boolean;
  authorId?: string | null;
  source?: string;
  reviewerId?: string | null;
  reviewComment?: string | null;
  dateReviewed?: string | null;
  provenance?: {
    prUrls?: string[];
    commsThreadIds?: string[];
    planEventIds?: string[];
  };
  supersedesChangeId?: string | null;
  ops?: StoryChangeOp[];
  dateCreated?: string;
  dateUpdated?: string;
};

export type StoryProject = {
  project_id: string;
  team_id: string;
  name: string;
  repo_slugs?: string[];
  archived?: boolean;
};

export type StoryTreeEntry = {
  documentId: string;
  kind: string;
  slug: string;
  title: string;
  status: string;
  surfaces?: string[];
  publishedRevisionId?: string | null;
  dateUpdated?: string;
};

/** Product Documentation behavior row (established or proposed). */
export type ProductBehavior = {
  behaviorId: string;
  actor?: string;
  action?: string;
  outcome?: string;
  status?: string;
};

/** Product Documentation section (SoR for product knowledge). */
export type ProductSection = {
  sectionId: string;
  teamId: string;
  projectId: string;
  kind: string;
  slug: string;
  title: string;
  status: string;
  summary?: string;
  usersMd?: string | null;
  behaviors?: ProductBehavior[];
  outOfScope?: string | null;
  openQuestions?: string | null;
  repoSlugs?: string[];
  relatedSectionIds?: string[];
  bodyMd?: string;
  publishedRevisionId?: string | null;
  revisionId?: string;
  source?: string;
  planId?: string | null;
  changeId?: string | null;
  dateCreated?: string;
  dateUpdated?: string;
};

export type ProductSectionTreeEntry = {
  sectionId: string;
  kind: string;
  slug: string;
  title: string;
  status: string;
  repoSlugs?: string[];
  publishedRevisionId?: string | null;
  dateUpdated?: string;
};

export type ProductDocChangeOp = {
  opId: string;
  changeId: string;
  op: 'add' | 'modify' | 'archive' | string;
  sectionId?: string | null;
  beforeRevisionId?: string | null;
  kind?: string | null;
  slug?: string | null;
  proposed?: Record<string, unknown>;
  sortOrder?: number;
};

export type ProductDocChange = {
  changeId: string;
  teamId: string;
  projectId: string;
  planId?: string | null;
  title: string;
  rationale?: string;
  status: string;
  autoApplied?: boolean;
  deviation?: boolean;
  requiresBehaviorConfirm?: boolean;
  authorId?: string | null;
  source?: string;
  reviewerId?: string | null;
  reviewComment?: string | null;
  dateReviewed?: string | null;
  provenance?: {
    prUrls?: string[];
    commsThreadIds?: string[];
    planEventIds?: string[];
  };
  supersedesChangeId?: string | null;
  ops?: ProductDocChangeOp[];
  dateCreated?: string;
  dateUpdated?: string;
};

/** Prerequisite group for QA sandbox / seed ops before a story runs. */
export type StoryPrereq = {
  prereqId: string;
  teamId: string;
  projectId: string;
  slug: string;
  title: string;
  status: string;
  ops?: Record<string, unknown>[];
  repoSlugs?: string[];
  sandboxProfile?: string | null;
  publishedRevisionId?: string | null;
  dateCreated?: string;
  dateUpdated?: string;
};

/** Team projects catalog CRUD (same rows as storiesApi.listProjects). */
export const projectsApi = {
  list: () => request<{ projects: StoryProject[] }>('/v1/projects'),
  create: (body: { name: string; repo_slugs?: string[] }) =>
    request<StoryProject>('/v1/projects', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (
    projectId: string,
    body: { name?: string; repo_slugs?: string[]; archived?: boolean },
  ) =>
    request<StoryProject>(`/v1/project/${encodeURIComponent(projectId)}/update`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

/**
 * Living User Stories documents — tree, CRUD, revisions, history, search, plan attach.
 * Side effects: GET/POST/PATCH against `/v1/stories…` and plan attach on `/v1/plan…`.
 */
export const storiesApi = {
  listProjects: () => request<{ projects: StoryProject[] }>('/v1/projects'),
  tree: (projectId: string, opts?: { surface?: string }) => {
    const sp = new URLSearchParams();
    if (opts?.surface) sp.set('surface', opts.surface);
    const qs = sp.toString();
    return request<{
      projectId: string;
      teamId: string;
      surface?: string | null;
      tree: Record<string, StoryTreeEntry[]>;
      coverage: { published: number; draft: number; withOpenQuestions: number };
    }>(
      `/v1/stories/projects/${encodeURIComponent(projectId)}/tree${qs ? `?${qs}` : ''}`,
    );
  },
  get: (documentId: string, opts?: { revision?: string; draftForPlan?: string }) => {
    const sp = new URLSearchParams();
    if (opts?.revision) sp.set('revision', opts.revision);
    if (opts?.draftForPlan) sp.set('draft_for_plan', opts.draftForPlan);
    const qs = sp.toString();
    return request<{
      document: StoryDocument;
      view: string;
      content: StoryDocument;
    }>(`/v1/stories/documents/${encodeURIComponent(documentId)}${qs ? `?${qs}` : ''}`);
  },
  create: (body: Record<string, unknown>) =>
    request<{ document: StoryDocument }>('/v1/stories/documents', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (documentId: string, body: Record<string, unknown>) =>
    request<{ document: StoryDocument }>(
      `/v1/stories/documents/${encodeURIComponent(documentId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
  revisions: (documentId: string) =>
    request<{ revisions: StoryDocument[] }>(
      `/v1/stories/documents/${encodeURIComponent(documentId)}/revisions`,
    ),
  history: (documentId: string) =>
    request<{
      documentId: string;
      changes: StoryChange[];
      revisions: StoryDocument[];
    }>(`/v1/stories/documents/${encodeURIComponent(documentId)}/history`),
  search: (body: { query: string; projectId?: string; limit?: number }) =>
    request<{ documents: StoryDocument[] }>('/v1/stories/search', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  attachToPlan: (planId: string, documentIds: string[]) =>
    request<{ plan: Plan }>(`/v1/plan/${encodeURIComponent(planId)}/stories/attach`, {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    }),
};

/**
 * Story Change proposals — judgment queue for Story Keeper deviations.
 * Side effects: list/get/create; approve/reject mutate published intent via `/v1/stories/changes…`.
 */
export const storyChangesApi = {
  list: (opts: {
    projectId?: string;
    status?: string;
    planId?: string;
    documentId?: string;
    limit?: number;
  }) => {
    const sp = new URLSearchParams();
    if (opts.projectId) sp.set('project_id', opts.projectId);
    if (opts.status) sp.set('status', opts.status);
    if (opts.planId) sp.set('plan_id', opts.planId);
    if (opts.documentId) sp.set('document_id', opts.documentId);
    if (opts.limit != null) sp.set('limit', String(opts.limit));
    const qs = sp.toString();
    return request<{ changes: StoryChange[] }>(
      `/v1/stories/changes${qs ? `?${qs}` : ''}`,
    );
  },
  get: (changeId: string) =>
    request<{ change: StoryChange }>(
      `/v1/stories/changes/${encodeURIComponent(changeId)}`,
    ),
  create: (body: Record<string, unknown>) =>
    request<{ change: StoryChange }>('/v1/stories/changes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approve: (changeId: string, body?: { comment?: string }) =>
    request<{ change: StoryChange }>(
      `/v1/stories/changes/${encodeURIComponent(changeId)}/approve`,
      { method: 'POST', body: JSON.stringify(body || {}) },
    ),
  reject: (changeId: string, body?: { comment?: string }) =>
    request<{ change: StoryChange }>(
      `/v1/stories/changes/${encodeURIComponent(changeId)}/reject`,
      { method: 'POST', body: JSON.stringify(body || {}) },
    ),
};

/**
 * Product Documentation — sections/behaviors SoR + Intent Change Proposals.
 * Side effects: GET/POST/PATCH against `/v1/product-docs…` (mirror of stories paths).
 */
export const productDocsApi = {
  tree: (projectId: string, opts?: { surface?: string }) => {
    const sp = new URLSearchParams();
    if (opts?.surface) sp.set('surface', opts.surface);
    const qs = sp.toString();
    return request<{
      projectId: string;
      teamId: string;
      surface?: string | null;
      tree: Record<string, ProductSectionTreeEntry[]>;
      coverage: { published: number; draft: number; withOpenQuestions: number };
    }>(
      `/v1/product-docs/projects/${encodeURIComponent(projectId)}/tree${qs ? `?${qs}` : ''}`,
    );
  },
  getSection: (sectionId: string) =>
    request<{
      section: ProductSection;
      view: string;
      content: ProductSection;
    }>(`/v1/product-docs/sections/${encodeURIComponent(sectionId)}`),
  listChanges: (opts: {
    projectId?: string;
    status?: string;
    planId?: string;
    sectionId?: string;
    limit?: number;
  }) => {
    const sp = new URLSearchParams();
    if (opts.projectId) sp.set('project_id', opts.projectId);
    if (opts.status) sp.set('status', opts.status);
    if (opts.planId) sp.set('plan_id', opts.planId);
    if (opts.sectionId) sp.set('section_id', opts.sectionId);
    if (opts.limit != null) sp.set('limit', String(opts.limit));
    const qs = sp.toString();
    return request<{ changes: ProductDocChange[] }>(
      `/v1/product-docs/changes${qs ? `?${qs}` : ''}`,
    );
  },
  createChange: (body: Record<string, unknown>) =>
    request<{ change: ProductDocChange }>('/v1/product-docs/changes', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  approve: (changeId: string, body?: { comment?: string }) =>
    request<{ change: ProductDocChange }>(
      `/v1/product-docs/changes/${encodeURIComponent(changeId)}/approve`,
      { method: 'POST', body: JSON.stringify(body || {}) },
    ),
  reject: (changeId: string, body?: { comment?: string }) =>
    request<{ change: ProductDocChange }>(
      `/v1/product-docs/changes/${encodeURIComponent(changeId)}/reject`,
      { method: 'POST', body: JSON.stringify(body || {}) },
    ),
};

/**
 * Story prerequisite groups — list/get/create/update for QA sandbox ops.
 * Side effects: `/v1/stories/prereqs…` and project-scoped list.
 */
export const storyPrereqsApi = {
  list: (projectId: string) =>
    request<{ prereqs: StoryPrereq[] }>(
      `/v1/stories/projects/${encodeURIComponent(projectId)}/prereqs`,
    ),
  get: (prereqId: string) =>
    request<{ prereq: StoryPrereq }>(
      `/v1/stories/prereqs/${encodeURIComponent(prereqId)}`,
    ),
  create: (body: Record<string, unknown>) =>
    request<{ prereq: StoryPrereq }>('/v1/stories/prereqs', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (prereqId: string, body: Record<string, unknown>) =>
    request<{ prereq: StoryPrereq }>(
      `/v1/stories/prereqs/${encodeURIComponent(prereqId)}`,
      { method: 'PATCH', body: JSON.stringify(body) },
    ),
};

/**
 * Infrastructure monitoring — live status + section/check catalog on `/devtools/v1/infra/*`.
 * Project-scoped; scheduler probes enabled checks every ~5 minutes server-side.
 */
export type InfraStatusLabel =
  | 'operational'
  | 'partial_outage'
  | 'major_outage'
  | 'unknown'
  | 'degraded';

export type InfraSection = {
  sectionId: string;
  name: string;
  sortOrder: number;
  seedKey?: string;
};

export type InfraCheck = {
  checkId: string;
  sectionId: string;
  name: string;
  url: string;
  method: string;
  expectStatus: number;
  timeoutMs: number;
  enabled: boolean;
  sortOrder: number;
  failStreak?: number;
  seedKey?: string;
};

/** Strip grain for `GET /infra/status/get?timescale=` (default scans). */
export type InfraTimescale = 'scans' | 'hours' | 'days';

export type InfraHistoryPoint = {
  key: string;
  status: InfraStatusLabel;
  okCount?: number;
  failCount?: number;
};

export type InfraHistory = {
  timescale: InfraTimescale;
  points: InfraHistoryPoint[];
};

export type InfraLastResult = {
  ok: boolean;
  httpStatus: number | null;
  latencyMs: number | null;
  error: string;
  checkedAt: string;
};

export type InfraStatusCheckRow = {
  check: InfraCheck;
  status: InfraStatusLabel;
  lastResult?: InfraLastResult | null;
  history: InfraHistory;
};

export type InfraStatusSection = {
  section: InfraSection;
  status: InfraStatusLabel;
  checks: InfraStatusCheckRow[];
};

export type InfraIncident = {
  incidentId: string;
  checkId: string;
  /** API values: `open` | `resolved`. */
  status: string;
  title: string;
  planId: string;
  dateOpened: string;
  dateResolved?: string | null;
  dateUpdated: string;
};

export type InfraStatusResponse = {
  overallStatus: InfraStatusLabel;
  sections: InfraStatusSection[];
  incidents: InfraIncident[];
  checkedAt: string;
};

export const infraApi = {
  /**
   * Aggregated live status: overall, sections with history strip, recent incidents.
   * Optional `timescale` selects strip grain (`scans` | `hours` | `days`); omitted → API default scans.
   */
  status: (projectId: string, opts?: { timescale?: InfraTimescale }) => {
    let path = withProjectQuery('/v1/infra/status/get', projectId);
    if (opts?.timescale) path += `&timescale=${encodeURIComponent(opts.timescale)}`;
    return request<InfraStatusResponse>(path);
  },
  /** Search/list monitoring sections for the project (optional name filter `q`). */
  listSections: (projectId: string, q?: string) => {
    let path = withProjectQuery('/v1/infra/sections/search', projectId);
    if (q) path += `&q=${encodeURIComponent(q)}`;
    return request<{ sections: InfraSection[] }>(path);
  },
  /** Create a named section grouping HTTP checks. */
  createSection: (projectId: string, body: { name: string; sortOrder?: number }) =>
    request<{ section: InfraSection }>('/v1/infra/sections/create', {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  /** Rename, reorder, or archive a section. */
  updateSection: (
    projectId: string,
    id: string,
    body: { name?: string; sortOrder?: number; archived?: boolean },
  ) =>
    request<{ section: InfraSection }>(
      `/v1/infra/section/${encodeURIComponent(id)}/update`,
      {
        method: 'POST',
        body: JSON.stringify(withProjectBody(body, projectId)),
      },
    ),
  /** Search/list HTTP checks (optional section + name filters). */
  listChecks: (projectId: string, opts?: { sectionId?: string; q?: string }) => {
    let path = withProjectQuery('/v1/infra/checks/search', projectId);
    if (opts?.sectionId) path += `&sectionId=${encodeURIComponent(opts.sectionId)}`;
    if (opts?.q) path += `&q=${encodeURIComponent(opts.q)}`;
    return request<{ checks: InfraCheck[] }>(path);
  },
  /** Register a public HTTP probe under a section. */
  createCheck: (
    projectId: string,
    body: {
      sectionId: string;
      name: string;
      url: string;
      expectStatus?: number;
      timeoutMs?: number;
      sortOrder?: number;
      enabled?: boolean;
    },
  ) =>
    request<{ check: InfraCheck }>('/v1/infra/checks/create', {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  /** Patch check fields (enable, archive, URL, expect status, etc.). */
  updateCheck: (projectId: string, id: string, body: Record<string, unknown>) =>
    request<{ check: InfraCheck }>(`/v1/infra/check/${encodeURIComponent(id)}/update`, {
      method: 'POST',
      body: JSON.stringify(withProjectBody(body, projectId)),
    }),
  /** Synchronously run all enabled checks for the project (manual refresh). */
  runNow: (projectId: string) =>
    request<{
      ok: boolean;
      message: string;
      checksRun: number;
      checksOk: number;
      checksFail: number;
    }>('/v1/infra/checks/run-now', {
      method: 'POST',
      body: JSON.stringify(withProjectBody({}, projectId)),
    }),
};
